import { Button, Checkbox, Chip, CircularProgress, IconButton, Link, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReplayIcon from '@mui/icons-material/Replay';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import DownloadIcon from '@mui/icons-material/Download';

const klopotekProductUrl = (impressionId) =>
  `http://192.168.135.68:8281/tmm/tenants/prod/#/productDetail/PRD/transcript%20Standard/${impressionId}/D/952/`;

// "Worst first" so sorting the XMP/Cover columns surfaces problems - same
// role as BookcardGenerator's noteCount for its Notes column. Any status not
// listed here (including the StageChip fallback's implicit "error") sorts as
// severity 0, consistent with StageChip treating unrecognized statuses as
// errors too.
export const STATUS_SEVERITY = { error: 0, mismatch: 1, not_delivered: 2, success: 3 };
export const statusSeverity = (status) => STATUS_SEVERITY[status] ?? 0;

// Status-only display otherwise - everything here is already resolved by the
// time this page is used, except the single re-run action below (restarts
// the whole xmp -> cover chain from the original source PDF; the pipeline
// has no way to re-run just one stage, so there's only one button, not two -
// showing two that do the same thing would be misleading).
function StageChip({ label, status }) {
  if (status === 'not_delivered') {
    return <Chip size="small" variant="outlined" label={`${label}: waiting for PDF`} />;
  }
  if (status === 'success') {
    return <Chip size="small" color="success" icon={<CheckCircleIcon />} label={`${label}: OK`} />;
  }
  if (status === 'mismatch') {
    return <Chip size="small" color="warning" icon={<WarningAmberIcon />} label={`${label}: Mismatch`} />;
  }
  return <Chip size="small" color="error" icon={<ErrorIcon />} label={`${label}: Error`} />;
}

function PipelineStatusRow({
  product, justTriggered, onRerun, onDownload, downloading, selected, onToggleSelect,
}) {
  const {
    ean, title, digitalAssetCollectionKey, impressionId, availabilityStatusTerm,
    xmp, cover, isStale, needsPickup, reprocessReason, processedPdfKey,
  } = product;
  // Same basename the backend uses for the zip entries and Content-Disposition.
  const downloadFilename = processedPdfKey
    ? processedPdfKey.substring(processedPdfKey.lastIndexOf('/') + 1)
    : `${ean}.pdf`;
  const bothOk = xmp.status === 'success' && cover.status === 'success';
  const canRerun = xmp.status !== 'not_delivered' || cover.status !== 'not_delivered';
  const rowBg = xmp.status === 'error' || cover.status === 'error'
    ? 'rgba(244,67,54,0.05)'
    : cover.status === 'mismatch'
      ? 'rgba(255,167,38,0.08)'
      : bothOk
        ? 'rgba(76,175,80,0.05)'
        : undefined;

  return (
    <TableRow sx={rowBg ? { backgroundColor: rowBg } : {}}>
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          checked={selected}
          disabled={!bothOk}
          onChange={() => onToggleSelect(ean)}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" noWrap title={title}>{title}</Typography>
        {digitalAssetCollectionKey && (
          <Typography
            variant="caption" color="text.secondary" noWrap
            title={`DAC key: ${digitalAssetCollectionKey}`}
            sx={{ display: 'block', fontFamily: 'monospace' }}
          >
            {digitalAssetCollectionKey}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {impressionId ? (
          <Tooltip title="Open in Klopotek">
            <Link
              variant="body2"
              underline="hover"
              href={klopotekProductUrl(impressionId)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontFamily: 'monospace' }}
            >
              {ean}
            </Link>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ean}</Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" noWrap title={availabilityStatusTerm || ''}>
          {availabilityStatusTerm || '–'}
        </Typography>
      </TableCell>
      <TableCell>
        <StageChip label="XMP" status={xmp.status} />
        {xmp.status === 'error' && xmp.message && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {xmp.message}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <StageChip label="Cover" status={cover.status} />
        {(cover.status === 'error' || cover.status === 'mismatch') && cover.message && (
          <Typography
            variant="caption"
            color={cover.status === 'mismatch' ? 'warning.dark' : 'error'}
            sx={{ display: 'block', mt: 0.5 }}
          >
            {cover.message}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Stack direction="column" spacing={0.5} alignItems="flex-start">
          {isStale && (
            <Chip
              size="small" color="info" variant="outlined"
              icon={<HistoryToggleOffIcon fontSize="small" />}
              label="Stale - reprocessing"
            />
          )}
          {needsPickup && (
            <>
              <Chip
                size="small" color="warning"
                icon={<NewReleasesIcon fontSize="small" />}
                label="Reprocessed - download again"
              />
              {reprocessReason && (
                <Typography variant="caption" color="text.secondary">
                  {reprocessReason}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
          <Tooltip title={bothOk ? 'Download this PDF' : 'Not ready - both stages must be OK'}>
            <span>
              <IconButton
                size="small"
                disabled={!bothOk || downloading}
                onClick={() => onDownload(ean, downloadFilename)}
              >
                {downloading ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small" variant="outlined" startIcon={<ReplayIcon fontSize="small" />}
            disabled={!canRerun}
            onClick={() => onRerun(ean)}
            sx={{ borderRadius: 999, px: 2 }}
          >
            Re-run
          </Button>
        </Stack>
        {justTriggered && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Rerun triggered - reload in a few minutes.
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

export default PipelineStatusRow;
